import ExcelJS from "exceljs";

// Samme struktur som i Python-skriptet
export type PrayerRow = {
  date: string; // "DD-MM-YYYY"
  hijriDate: string; // "D-M-YYYY"
  fajr: string;
  fajr_endtime: string;
  duhr: string;
  asr: string;
  asr_1x_shadow: string;
  asr_2x_shadow: string;
  maghrib: string;
  isha: string;
  locationId: number;
  prayerTimeTable: number;
};

export type AsrFilterOptions = {
  onlyShafi: boolean;  // Kun Shafi'i (1x) - fjerner Hanafi
  onlyHanafi: boolean; // Kun Hanafi (2x) - fjerner Shafi'i
};

type ColumnKey = "day" | "weekday" | "fajr" | "fajr_endtime" | "duhr" | "asr_1x_shadow" | "asr_2x_shadow" | "maghrib" | "isha";

const ALL_COLUMNS: ColumnKey[] = [
  "day",
  "weekday",
  "fajr",
  "fajr_endtime",
  "duhr",
  "asr_1x_shadow",
  "asr_2x_shadow",
  "maghrib",
  "isha",
];

function getHeaderTitles(asrFilter?: AsrFilterOptions): Record<ColumnKey, string> {
  return {
    day: "Dato",
    weekday: "Ukedag",
    fajr: "Fajr",
    fajr_endtime: "Soloppgang",
    duhr: "Dhuhr",
    asr_1x_shadow: asrFilter?.onlyShafi ? "Asr" : "Asr\nShafi'i",
    asr_2x_shadow: asrFilter?.onlyHanafi ? "Asr" : "Asr\nHanafi",
    maghrib: "Maghrib",
    isha: "Isha",
  };
}

function getOutputColumns(asrFilter?: AsrFilterOptions): ColumnKey[] {
  return ALL_COLUMNS.filter((col) => {
    if (col === "asr_1x_shadow" && asrFilter?.onlyHanafi) return false;
    if (col === "asr_2x_shadow" && asrFilter?.onlyShafi) return false;
    return true;
  });
}

function getColumnWidths(asrFilter?: AsrFilterOptions): number[] {
  const baseWidths: Record<ColumnKey, number> = {
    day: 8.0,
    weekday: 10.0,
    fajr: 9.5,
    fajr_endtime: 14.0,
    duhr: 9.5,
    asr_1x_shadow: 13.0,
    asr_2x_shadow: 13.0,
    maghrib: 11.0,
    isha: 11.0,
  };
  return getOutputColumns(asrFilter).map((col) => baseWidths[col]);
}

const MONTH_NAMES_NO: Record<number, string> = {
  1: "Januar",
  2: "Februar",
  3: "Mars",
  4: "April",
  5: "Mai",
  6: "Juni",
  7: "Juli",
  8: "August",
  9: "September",
  10: "Oktober",
  11: "November",
  12: "Desember",
};

const WEEKDAY_NO: Record<number, string> = {
  0: "Man",
  1: "Tir",
  2: "Ons",
  3: "Tor",
  4: "Fre",
  5: "Lør",
  6: "Søn",
};

function parseDate(dateStr: string): Date {
  // "DD-MM-YYYY"
  const [dd, mm, yyyy] = dateStr.split("-");
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
}

function getLastSunday(year: number, month: number): Date {
  // month: 1-12
  const lastDay = new Date(year, month, 0); // last day in that month
  let d = lastDay;
  while (d.getDay() !== 0) {
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
  }
  return d;
}

type GroupedRows = Record<string, { date: Date; row: PrayerRow }[]>;

/**
 * Lager en stylet Excel-fil (én sheet per måned) med samme logikk
 * som Python-versjonen (ekstremperioder, sommertid, fotnote, osv).
 */
export async function generateStyledExcel(
  rows: PrayerRow[],
  cityName: string,
  year: number,
  asrFilter?: AsrFilterOptions,
) {
  const OUTPUT_COLUMNS = getOutputColumns(asrFilter);
  const COLUMN_WIDTHS = getColumnWidths(asrFilter);
  if (!rows.length) return;

  // Sorter og grupper etter måned
  const grouped: GroupedRows = {};
  for (const r of rows) {
    const d = parseDate(r.date);
    const key = `${d.getFullYear()}-${d.getMonth() + 1}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push({ date: d, row: r });
  }

  // Sørg for sortering per måned
  for (const key of Object.keys(grouped)) {
    grouped[key].sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  const lastSundayMarch = getLastSunday(year, 3);
  const lastSundayOctober = getLastSunday(year, 10);
  const workbook = new ExcelJS.Workbook();

  // Styles
  const headerFont = { bold: true, size: 14, color: { argb: "FF000000" } };
  const headerFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFCCFFCC" },
  };
  const monthHeaderFont = {
    bold: true,
    size: 18,
    color: { argb: "FF000000" },
  };
  const monthHeaderFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFFFCC00" },
  };
  const redFont = { color: { argb: "FFFF0000" } };
  const redFill = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFFFC7CE" },
  };
  const centerAlign = {
    horizontal: "center" as const,
    vertical: "middle" as const,
    wrapText: true,
  };
  const stripeWhite = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFFFFFFF" },
  };
  const stripeGray = {
    type: "pattern" as const,
    pattern: "solid" as const,
    fgColor: { argb: "FFD9D9D9" },
  };
  const dataFont = { size: 13 };

  // Først: finn ekstremperiode på tvers av året
  let extremeStarted = false;
  let extremeEnded = false;
  let prevIshaMissing = false;
  let extremeStartMonth: number | null = null;
  let extremeEndMonth: number | null = null;
  const extremeMonths = new Set<number>();

  for (let month = 1; month <= 12; month++) {
    const key = `${year}-${month}`;
    const monthRows = grouped[key];
    if (!monthRows || monthRows.length === 0) continue;

    let hasExtremeThisMonth = false;
    for (const { date, row } of monthRows) {
      const isha = row.isha;
      if (isha === "--:--") {
        hasExtremeThisMonth = true;
      }
      if (!extremeStarted && isha === "--:--") {
        extremeStarted = true;
        prevIshaMissing = true;
        if (extremeStartMonth === null) extremeStartMonth = month;
      } else if (extremeStarted && !extremeEnded) {
        if (isha === "--:--") {
          prevIshaMissing = true;
        } else {
          if (prevIshaMissing && extremeEndMonth === null) {
            extremeEnded = true;
            extremeEndMonth = month;
          }
          prevIshaMissing = false;
        }
      }
    }
    if (hasExtremeThisMonth) {
      extremeMonths.add(month);
    }
  }

  // Reset til rad-styling
  extremeStarted = false;
  extremeEnded = false;
  prevIshaMissing = false;

  // Flag for "neste rad skal ha rød Fajr"
  let applyNextRowRedFajrGlobal = false;

  for (let month = 1; month <= 12; month++) {
    const key = `${year}-${month}`;
    const monthRows = grouped[key];
    if (!monthRows || monthRows.length === 0) continue;

    const ws = workbook.addWorksheet(
      `${String(month).padStart(2, "0")} ${MONTH_NAMES_NO[month]}`,
    );

    // Page setup A4
    ws.pageSetup = {
      paperSize: 9, // A4
      orientation: "portrait" as const,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    };
    ws.views = [{ showGridLines: false }];

    let currentRow = 1;

    // Tittel: "Oslo - Januar 2026"
    ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
    const titleCell = ws.getCell(currentRow, 1);
    titleCell.value = `${cityName} - ${MONTH_NAMES_NO[month]} ${year}`;
    titleCell.font = monthHeaderFont;
    titleCell.fill = monthHeaderFill;
    titleCell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    ws.getRow(currentRow).height = 26;
    currentRow++;

    // Header-rad
    const headerRowIndex = currentRow;
    const HEADER_TITLES = getHeaderTitles(asrFilter);
    OUTPUT_COLUMNS.forEach((field, i) => {
      const colIdx = i + 1;
      const cell = ws.getCell(currentRow, colIdx);
      cell.value = HEADER_TITLES[field];
      cell.font = headerFont;
      if (field === "day" || field === "weekday") {
        cell.fill = monthHeaderFill;
      } else {
        cell.fill = headerFill;
      }
      cell.alignment = centerAlign;
    });
    ws.getRow(headerRowIndex).height = 35;
    currentRow++;

    let monthDataRowIndex = 0;


    // Data-rader
    for (const { date, row } of monthRows) {
      const rowStart = currentRow;

      // Skal denne raden ha rød Fajr pga forrige ekstrem rad?
      const nextRowShouldHaveRedFajr = applyNextRowRedFajrGlobal;
      applyNextRowRedFajrGlobal = false;

      let isExtremeStartRow = false;
      let isExtremeEndRow = false;
      const isha = row.isha;

      // Ekstremperiode-deteksjon på radnivå
      if (!extremeStarted && isha === "--:--") {
        extremeStarted = true;
        prevIshaMissing = true;
        isExtremeStartRow = true;
      } else if (extremeStarted && !extremeEnded) {
        if (isha === "--:--") {
          prevIshaMissing = true;
        } else {
          if (prevIshaMissing) {
            extremeEnded = true;
            isExtremeEndRow = true;
          }
          prevIshaMissing = false;
        }
      }

      if (isExtremeStartRow || isExtremeEndRow) {
        applyNextRowRedFajrGlobal = true;
      }

      // Skriv celler
      OUTPUT_COLUMNS.forEach((field, i) => {
        const colIdx = i + 1;
        const cell = ws.getCell(currentRow, colIdx);

        if (field === "day") {
          cell.value = date.getDate();
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "weekday") {
          cell.value =
            WEEKDAY_NO[date.getDay() === 0 ? 6 : date.getDay() - 1]; // map JS 0=Sun => 6=Søn
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "fajr") {
          cell.value = row.fajr;
          cell.alignment = { horizontal: "center" };
          if (nextRowShouldHaveRedFajr) {
            cell.font = { ...dataFont, color: { argb: "FFFF0000" } };
          } else {
            cell.font = dataFont;
          }
        } else if (field === "fajr_endtime") {
          cell.value = row.fajr_endtime;
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "duhr") {
          cell.value = row.duhr;
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "asr_1x_shadow") {
          cell.value = row.asr_1x_shadow;
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "asr_2x_shadow") {
          cell.value = row.asr_2x_shadow;
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "maghrib") {
          cell.value = row.maghrib;
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        } else if (field === "isha") {
          cell.value = row.isha;
          cell.alignment = { horizontal: "center" };
          cell.font = dataFont;
        }
      });

      // DST-linje: siste søndag i mars / oktober
      const sameDay =
        date.getFullYear() === lastSundayMarch.getFullYear() &&
        date.getMonth() === lastSundayMarch.getMonth() &&
        date.getDate() === lastSundayMarch.getDate();

      const sameDayOct =
        date.getFullYear() === lastSundayOctober.getFullYear() &&
        date.getMonth() === lastSundayOctober.getMonth() &&
        date.getDate() === lastSundayOctober.getDate();

      if (sameDay || sameDayOct) {
        for (let colIdx = 1; colIdx <= OUTPUT_COLUMNS.length; colIdx++) {
          const cell = ws.getCell(rowStart, colIdx);
          cell.font = {
            size: 13,
            bold: true,
            color: { argb: "FFFF0000" },
          };
        }
      }

      // bakgrunn
      let fill;
      if (isExtremeStartRow || isExtremeEndRow) {
        fill = redFill;
      } else {
        fill = monthDataRowIndex % 2 === 0 ? stripeWhite : stripeGray;
      }

      for (let colIdx = 1; colIdx <= OUTPUT_COLUMNS.length; colIdx++) {
        const cell = ws.getCell(rowStart, colIdx);
        cell.fill = fill;

        // enkle tynne borders
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      }

      ws.getRow(rowStart).height = 22;
      currentRow++;
      monthDataRowIndex++;
    }

    // Footnote
    const footRow = currentRow;
    ws.mergeCells(footRow, 1, footRow, OUTPUT_COLUMNS.length);
    const footCell = ws.getCell(footRow, 1);
    footCell.value = `www.mawaqeet.no ${year} - bønnetider i Norge - generert av iBønn`;
    footCell.font = { size: 11, color: { argb: "FF666666" }, italic: true };
    footCell.alignment = { horizontal: "right", vertical: "middle" };
    ws.getRow(footRow).height = 18;
    currentRow++;

    // Sommertid-tekst
    if (month === 3) {
      currentRow++; // blank
      ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
      const cell = ws.getCell(currentRow, 1);
      cell.value = "Klokken stilles én time frem";
      cell.font = { italic: true, color: { argb: "FFFF0000" } };
      currentRow += 2;
    }

    if (month === 10) {
      currentRow++; // blank
      ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
      const cell = ws.getCell(currentRow, 1);
      cell.value = "Klokken stilles én time tilbake (sommertidslutt)";
      cell.font = { italic: true, color: { argb: "FFFF0000" } };
      currentRow += 2;
    }

    // Ekstremperiode start
    if (extremeStartMonth !== null && month === extremeStartMonth) {
      ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
      let cell = ws.getCell(currentRow, 1);
      cell.value = "Ekstremperiode start";
      cell.font = { bold: true, color: { argb: "FFFF0000" } };
      currentRow++;
      ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
      cell = ws.getCell(currentRow, 1);
      cell.value = "Fajr: Nisf-ul-layl";
      cell.font = { bold: true, color: { argb: "FFFF0000" } };
      currentRow++;
    }

    // Ekstremperiode slutt
    if (extremeEndMonth !== null && month === extremeEndMonth) {
      ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
      let cell = ws.getCell(currentRow, 1);
      cell.value = "Ekstremperiode slutt";
      cell.font = { bold: true, color: { argb: "FFFF0000" } };
      currentRow++;
      ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
      cell = ws.getCell(currentRow, 1);
      cell.value = "Fajr: 18 grader";
      cell.font = { bold: true, color: { argb: "FFFF0000" } };
      currentRow++;
    }

    // Ekstremperiode-forklaring - bare for måneder i ekstremperioden
    if (extremeMonths.has(month)) {
      currentRow++;
      const explanationLines = [
        "Under ekstremperioden har Isha fellestid med Maghrib fram til Fajr. Man deler da perioden mellom solnedgang og Fajr i",
        "to, hvor den første delen gis til Maghrib og den andre til Isha. Hvis Isha er veldig sen og det er vanskelig å vente kan man",
        "be Isha med et passende mellomrom etter Maghrib.",
      ];

      for (const text of explanationLines) {
        ws.mergeCells(currentRow, 1, currentRow, OUTPUT_COLUMNS.length);
        const cell = ws.getCell(currentRow, 1);
        cell.value = text;
        cell.font = { bold: true };
        currentRow++;
      }
      currentRow++;
    }

    // Kolonnebredder
    COLUMN_WIDTHS.forEach((w, i) => {
      const col = ws.getColumn(i + 1);
      col.width = w;
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${cityName} ${year}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

